import { useL10n } from "@ews/react-localization-context";
import { IonButton, IonCol, IonGrid, IonIcon, IonInput, IonItem } from "@ionic/react";
import { SwiperSlide, useSwiper } from 'swiper/react';

import
{
    NoDeviceNumber,
    NumberGroup
} from '@ews/zlt-events';

import { LicenseType, SystemGroup } from "@ews/react-data";
import { reload } from "ionicons/icons";
import React, { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import type { FilterProps } from "../../../pages/System";
import { useCurrentSystemId } from "../../Authorization";
import FilterList, { isFilterActive } from "../../FilterList/FilterList";
import { FilterState } from "../../FilterList/types";
import { useHasLicense } from "../../HasLicense";
import { useNamedRoutes } from "../../NamedRoutes";
import { usePersistentState } from "../../Persistence";
import { useSystem } from "../../ReactData/system";
import { useIsMobile } from "../../Responsive";
import SettingsSwiper from "../../Settings/SettingsComponents/SettingsSwiper/SettingsSwiper";
import SettingsTextInput from "../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput";
import { SwiperRedirectPageOneListener } from "../../SwiperRedirectPageOneListener/SwiperRedirectPageOneListener";
import GroupItem from "../GroupItem/GroupItem";
import { FilterCategories, FilterGroups } from "../types";
import groupStyle from "./GroupOverview.module.scss";
import { firstLetterUpperCase, zoneCompare } from "./helper";

type ListItem = { position: number, list: SystemGroup[], label: string; };

type List = {
    [NumberGroup.Zone]: ListItem;
    [NumberGroup.ActuationDevice]: ListItem;
    [NumberGroup.TransmissionDevice]: ListItem;
    [NumberGroup.AlarmingDevice]: ListItem;
    [NumberGroup.FloodingZone]: ListItem;
};

const SliderComponentDesktop: FC<{
    children: React.ReactNode,
    className?: string;
}> = ({ children, className }) => <section className={`${groupStyle["slider-section-componente"]} ${className}`} > {children}</section >;

const Container: FC<{ className?: string, children: ReactNode, style?: Record<string, string>; }> = ({ children, style, className }) =>
{
    const mobileView = useIsMobile();
    return mobileView ? <>{children}</> : <div className={className} style={style}>{children}</div>;
};

type RangeFilter = FilterGroups & FilterState;

const standardFilter: Record<string | number, any> = {};
const ignoreKeys: (keyof RangeFilter)[] = ["isFilterOpen"];

const GroupOverview: React.FC<{
    groups: SystemGroup[],
} & FilterProps> = ({
    groups,
    filter,
    onIsOpen,
    onFilterActive
}) =>
    {

        const { params } = useNamedRoutes();
        const { page } = params();

        const systemId = useCurrentSystemId();
        const system = useSystem(systemId);

        const slideToRef = useRef<number>(0);
        const swiperInputRef = useRef<HTMLIonInputElement>(null);
        const hasDetailViewLicense = useHasLicense(LicenseType.DETAIL_VIEW);

        const [currentPramPage, setCurrentPramPage] = useState<number>(page ? Number(page) : 0);


        for (const numberGroup of FilterCategories) {
            standardFilter[numberGroup] = { zoneFrom: "1", zoneTo: NoDeviceNumber.toString() };
        }

        const [rangeFilter, setRangeFilter] = usePersistentState<FilterGroups & FilterState>('system-group:filter', standardFilter, systemId);

        const onIsFilterOpen = useCallback((state: boolean) => { onIsOpen && onIsOpen(state, "main"); }, [onIsOpen]);
        const onResetFilter = useCallback(() =>
        {
            setRangeFilter(() => standardFilter);
        }, [setRangeFilter]);

        const isFilterChanged = isFilterActive<FilterGroups & FilterState>(rangeFilter, standardFilter, ignoreKeys);

        useEffect(() =>
        {
            if (filter?.isFilterActive !== isFilterChanged) {
                onFilterActive && onFilterActive(isFilterChanged);
            }
        }, [isFilterChanged, filter, onFilterActive]);

        const groupList: List = {
            [NumberGroup.FloodingZone]: { position: 1, list: [], label: "[SID_MNU_TAB_EXT_SEC]" },
            [NumberGroup.Zone]: { position: 2, list: [], label: "[SID_MNU_TAB_GROUPS]" },
            [NumberGroup.ActuationDevice]: { position: 3, list: [], label: "[SID_MNU_TAB_CONTROLS]" },
            [NumberGroup.TransmissionDevice]: { position: 4, list: [], label: "[SID_MNU_TAB_TR_DEVICES]" },
            [NumberGroup.AlarmingDevice]: { position: 5, list: [], label: "[SID_MNU_TAB_AL_DEVICES]" },
        };

        function sortGroupList(a: [string, ListItem], b: [string, ListItem])
        {
            const [keyA, { position: positionA }] = a;
            const [keyB, { position: positionB }] = b;

            return positionA - positionB;
        }

        function getFirstPageLabel(groups: List)
        {
            const list = Object.entries(groups).filter(([key, value]) =>
            {
                return !(Number(key) === NumberGroup.FloodingZone && !system.hasFloodingZone);
            });

            return list.sort(sortGroupList)[0][1].label;
        }

        const mobileView = useIsMobile();
        const { translate: t } = useL10n();

        const [_currentPage, setCurrentPage] = useState("");
        const currentPage = _currentPage || getFirstPageLabel(groupList);

        const SwiperButtonNext: React.FC<{ swipeRef: any; }> = ({ swipeRef }) =>
        {
            const swiper = useSwiper();
            return <IonInput
                label=""
                ref={swipeRef}
                onClick={(e) =>
                {
                    swiper.slideToLoop(slideToRef.current);
                }}
                hidden />;
        };

        groups.forEach((group) =>
        {
            const type = group.numberGroup as keyof List;
            const filter = rangeFilter[type];

            if (filter) {
                if (group.numberGroup === NumberGroup.FloodingZone) {

                    group = { ...group } as SystemGroup;
                    group.elements = group.elements?.filter(element => zoneCompare(element.id!, parseInt(filter.zoneFrom || "1")) >= 0 &&
                        zoneCompare(element.id!, parseInt(filter.zoneTo || `${NoDeviceNumber}`)) <= 0);

                    if (group.elements?.length) groupList[type].list.push(group);

                } else if (zoneCompare(group.id!, parseInt(filter.zoneFrom || "1")) >= 0 &&
                    zoneCompare(group.id!, parseInt(filter.zoneTo || `${NoDeviceNumber}`)) <= 0) {
                    groupList[type].list.push(group);
                }
            } else {
                groupList[type].list.push(group);
            }
        });



        const SwiperComponent = mobileView ? SettingsSwiper : IonGrid;
        const SliderComponent = mobileView ? SwiperSlide : SliderComponentDesktop;

        return (<>
            <div className={`${groupStyle["group-card-content-container"]} ${mobileView ? '' : 'ion-padding'}`} >
                <SwiperRedirectPageOneListener
                    swiperInputRef={swiperInputRef}
                    slideToRef={slideToRef}
                    page={currentPramPage}
                    link={'system:elements'}
                />
                {
                    <div hidden={mobileView ? true : !filter?.isFilterOpen} >
                        <FilterList
                            onIsOpen={onIsFilterOpen}
                            onReset={onResetFilter}
                            isOpen={filter?.isFilterOpen || false}
                        >

                            {FilterCategories.map((key) =>
                            {
                                const numberGroup = key as keyof FilterGroups;
                                const filter = rangeFilter[numberGroup];

                                if (key === NumberGroup.FloodingZone && !system.hasFloodingZone) {
                                    return <React.Fragment key={key} />;
                                }

                                return <IonCol key={key}>
                                    <IonItem>
                                        <div style={{ width: "10em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t(`[REACT_GROUP_RANGE_PICKER_${key}]`)}</div>
                                        <SettingsTextInput
                                            text=""
                                            lines="none"
                                            textAlign="left"
                                            className={
                                                groupStyle["remove-arrows"]
                                            }
                                            inputType="number"
                                            value={filter?.zoneFrom}
                                            onChange={(value) =>
                                            {
                                                const newState = { ...rangeFilter };

                                                newState[key]!.zoneFrom = value;
                                                setRangeFilter(newState);
                                            }}
                                        /> -
                                        <SettingsTextInput
                                            text=""
                                            lines="none"
                                            textAlign="left"
                                            className={
                                                groupStyle["remove-arrows"]
                                            }
                                            inputType="number"
                                            value={filter?.zoneTo}
                                            onChange={(value) =>
                                            {
                                                const newState = { ...rangeFilter };

                                                newState[key]!.zoneTo = value;
                                                setRangeFilter(newState);
                                            }}
                                        />
                                        {mobileView ?
                                            <IonButton
                                                onClick={() =>
                                                {
                                                    const newState = { ...rangeFilter };

                                                    newState[key] = standardFilter[key];
                                                    setRangeFilter(newState);
                                                }}
                                            ><IonIcon icon={reload} /></IonButton> :
                                            null}
                                    </IonItem>
                                </IonCol>;
                            })}
                        </FilterList>
                    </div>
                }

                <SwiperComponent
                    loop
                    className={`${!mobileView && filter?.isFilterOpen ? groupStyle["filter"] : ""} ${groupStyle["group-swiper"]}`}
                    value={currentPramPage}
                    onSlideChange={(value) => setCurrentPramPage(value)}
                >

                    {mobileView && hasDetailViewLicense && <SwiperButtonNext swipeRef={swiperInputRef} />}
                    {mobileView && hasDetailViewLicense ? <SliderComponent key={'group-overview-slider-main'} className={mobileView ? groupStyle["slide-height"] : ""}>
                        <div className={groupStyle.buttonContainer}>
                            {Object.entries(groupList)
                                .sort(sortGroupList)
                                .map(([key, { list, label, position }], index) =>
                                {
                                    if (Number(key) === NumberGroup.FloodingZone) {
                                        if (!system.hasFloodingZone) return <React.Fragment key={index} />;
                                    }
                                    return <div key={index} className={groupStyle["button-wrapper"]} data-x={label}>
                                        <IonButton
                                            expand="full"
                                            color='primary'
                                            className={groupStyle["gp-btn"]}
                                            onClick={() =>
                                            {
                                                let swiperIndex = position;
                                                if (!system.hasFloodingZone) {
                                                    swiperIndex -= 1;
                                                }
                                                slideToRef.current = swiperIndex;
                                                swiperInputRef.current?.click();
                                            }}
                                        >
                                            <div className={groupStyle["pull-left"]}>{t(firstLetterUpperCase(label))}</div>

                                        </IonButton>
                                    </div>;
                                })}
                        </div>
                    </SliderComponent> : null}

                    <Container className={groupStyle['container']}>

                        <Container className={groupStyle['group-container']}>
                            {
                                mobileView ? <>
                                    {
                                        Object.entries(groupList)
                                            .sort(sortGroupList)
                                            .map(([key, { list, label }]) =>
                                            {
                                                if (parseInt(key) === NumberGroup.FloodingZone && !system.hasFloodingZone) {
                                                    return <React.Fragment key={key} />;
                                                }

                                                return <SliderComponent key={key} className={groupStyle['group-swiper']}>
                                                    <GroupItem groups={list} title={label} />
                                                </SliderComponent>;
                                            })

                                    }

                                </> : <>
                                    {
                                        Object.entries(groupList).map(([key, { list, label }]) =>
                                        {
                                            if (parseInt(key) === NumberGroup.FloodingZone && !system.hasFloodingZone) {
                                                return <React.Fragment key={key} />;
                                            }

                                            return <GroupItem key={key} className={currentPage === label ? "" : groupStyle.none} groups={list} title={label} />;
                                        })

                                    }

                                </>
                            }
                        </Container>
                        {!mobileView ? <div className={groupStyle.buttonContainer}>
                            {Object.entries(groupList)
                                .sort(sortGroupList)
                                .map(([key, { list, label }], index) =>
                                {
                                    if (parseInt(key) === NumberGroup.FloodingZone && !system.hasFloodingZone) {
                                        return <React.Fragment key={index} />;
                                    }

                                    return <div key={index}>
                                        <IonButton
                                            expand="full"
                                            color={currentPage === label ? 'secondary' : 'primary'}
                                            onClick={() => { setCurrentPage(label); }}
                                        >{firstLetterUpperCase(t(label))}</IonButton>
                                    </div>;
                                })}
                        </div> : null}
                    </Container>
                </SwiperComponent>
            </div >
        </>
        );
    };
export default GroupOverview;
